import axios from 'axios';
import { getHoldings } from './assetHoldingStore';
import { todayYMD } from './assetTradingCalendar';
import { uploadWebUserBackup } from './assetHoldingBackupCloud';
import { AUTO_BACKUP_LOCAL_BASE_KEY, scopedAssetKey } from './assetStorageScope';

const AUTO_BACKUP_API = '/api/holding-auto-backup';

function authHeaders() {
  const token = localStorage.getItem('auth_token');
  if (!token) return null;
  return { Authorization: `Bearer ${token}` };
}

function readLocalState() {
  try {
    const raw = localStorage.getItem(scopedAssetKey(AUTO_BACKUP_LOCAL_BASE_KEY));
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch (e) {
    return {};
  }
}

function writeLocalState(patch) {
  const next = { ...readLocalState(), ...(patch || {}) };
  try {
    localStorage.setItem(scopedAssetKey(AUTO_BACKUP_LOCAL_BASE_KEY), JSON.stringify(next));
  } catch (e) {
    // ignore
  }
  return next;
}

export function getAutoBackupLocalState() {
  return readLocalState();
}

export function markAutoBackupPromptDismissed() {
  return writeLocalState({ promptDismissed: true });
}

function unwrap(res) {
  const body = res?.data;
  if (!body || typeof body !== 'object') {
    return { ok: false, message: '服务响应异常' };
  }
  if (Number(body.code) === 0) {
    return { ok: true, message: body.message || 'ok', data: body.data };
  }
  return { ok: false, message: body.message || '请求失败', code: body.code, data: body.data };
}

export async function fetchAutoBackupStatus() {
  const headers = authHeaders();
  if (!headers) return { ok: false, message: '请先登录', code: -1 };
  try {
    const res = await axios.get(`${AUTO_BACKUP_API}/status`, { headers });
    return unwrap(res);
  } catch (error) {
    return { ok: false, message: error.message || '网络请求失败' };
  }
}

export async function fetchAutoBackupPassword() {
  const headers = authHeaders();
  if (!headers) return { ok: false, message: '请先登录', code: -1 };
  try {
    const res = await axios.get(`${AUTO_BACKUP_API}/password`, { headers });
    return unwrap(res);
  } catch (error) {
    return { ok: false, message: error.message || '网络请求失败' };
  }
}

/** 开启自动备份并保存备份密码到服务器（加密） */
export async function enableAutoBackup(password) {
  const headers = authHeaders();
  if (!headers) return { ok: false, message: '请先登录', code: -1 };
  try {
    const res = await axios.put(AUTO_BACKUP_API, {
      enabled: true,
      password
    }, { headers });
    return unwrap(res);
  } catch (error) {
    return { ok: false, message: error.message || '网络请求失败' };
  }
}

/** 关闭自动备份并清除服务器上的备份密码 */
export async function disableAutoBackup() {
  const headers = authHeaders();
  if (!headers) return { ok: false, message: '请先登录', code: -1 };
  try {
    const res = await axios.put(AUTO_BACKUP_API, { enabled: false }, { headers });
    return unwrap(res);
  } catch (error) {
    return { ok: false, message: error.message || '网络请求失败' };
  }
}

/**
 * 每日自动备份：北京时间自然日最多成功一次。
 * 时机：进入资产中心且已登录、已开启、有持仓时调用。
 */
export async function tryDailyAutoBackup(opts = {}) {
  const force = Boolean(opts.force);
  const headers = authHeaders();
  if (!headers) {
    return { ok: false, skipped: true, reason: 'not_logged_in', message: '未登录，跳过自动备份' };
  }

  const today = todayYMD();
  const local = readLocalState();
  if (!force && local.lastSuccessYmd === today) {
    return {
      ok: true,
      skipped: true,
      reason: 'already_today',
      message: '今日已自动备份',
      lastSuccessAt: local.lastSuccessAt || null
    };
  }

  const holdings = getHoldings();
  if (!Array.isArray(holdings) || holdings.length === 0) {
    return { ok: false, skipped: true, reason: 'no_holdings', message: '暂无持仓，跳过自动备份' };
  }

  const status = await fetchAutoBackupStatus();
  if (!status.ok) {
    writeLocalState({ lastError: status.message || '无法读取自动备份状态' });
    return { ...status, skipped: true, reason: 'status_failed' };
  }
  if (!status.data?.enabled || !status.data?.hasPassword) {
    return { ok: true, skipped: true, reason: 'disabled', message: '未开启自动备份' };
  }

  const pwdRes = await fetchAutoBackupPassword();
  if (!pwdRes.ok || !pwdRes.data?.password) {
    writeLocalState({ lastError: pwdRes.message || '无法获取备份密码' });
    return { ...pwdRes, skipped: false, reason: 'password_failed' };
  }

  const up = await uploadWebUserBackup(pwdRes.data.password);
  if (!up.ok) {
    writeLocalState({ lastError: up.message || '自动备份失败' });
    return { ...up, skipped: false, reason: 'upload_failed' };
  }

  const nowIso = new Date().toISOString();
  writeLocalState({
    lastSuccessYmd: today,
    lastSuccessAt: nowIso,
    lastError: '',
    lastHoldingCount: up.holdingCount ?? null
  });
  return {
    ok: true,
    skipped: false,
    message: up.message || '自动备份成功',
    holdingCount: up.holdingCount,
    updatedAt: up.updatedAt || nowIso
  };
}
